import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Building2, FileText, Users, ChevronRight, Search, Plus,
  Shield, Loader2, Calendar, Activity, LogOut, RefreshCw,
  Target, Clock, Milestone, Info, Edit, Globe, MapPin, X, Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// eTMF Vault Logo URL
const ETMF_VAULT_LOGO = "https://www.vetforcesolutions.com/backend/uploads/8e6f835bdae1ff1ee6c1d6b663d984dc.png";

const ROLE_DISPLAY_NAMES = {
  system_admin: 'System Administrator',
  admin: 'Administrator',
  tmf_lead: 'TMF Lead',
  ctm: 'Clinical Trial Manager',
  cra: 'CRA / Monitor',
  cdm: 'Clinical Data Manager',
  qc_reviewer: 'QC Reviewer',
  qa_auditor: 'QA / Auditor',
  regulatory: 'Regulatory Affairs',
  site_user: 'Site User',
  vendor: 'CRO / Vendor User',
  inspector: 'Inspector',
  clinical_monitor: 'Clinical Monitor',
  data_manager: 'Data Manager',
  quality_assurance: 'Quality Assurance',
  regulatory_affairs: 'Regulatory Affairs',
  sponsor_representative: 'Sponsor Representative'
};

const STATUS_COLORS = {
  active: 'bg-green-500',
  planning: 'bg-blue-500',
  closed: 'bg-slate-500',
  paused: 'bg-amber-500'
};

const THERAPEUTIC_AREAS = [
  'Oncology',
  'Cardiology',
  'Neurology',
  'Immunology',
  'Infectious Disease',
  'Endocrinology',
  'Respiratory',
  'Gastroenterology',
  'Dermatology',
  'Ophthalmology',
  'Rare Diseases',
  'Pediatrics',
  'Other'
];

const DEFAULT_COUNTRIES = [
  'United States',
  'United Kingdom',
  'Germany',
  'France',
  'Japan',
  'Canada',
  'Australia',
  'Italy',
  'Spain',
  'Netherlands',
  'Switzerland',
  'Belgium',
  'India',
  'China',
  'Brazil'
];

// Tab completion indicator component - defined outside to avoid re-creation on each render
const TabStatus = ({ completed }) => (
  completed ? (
    <Check className="w-3 h-3 text-green-400 ml-1" />
  ) : (
    <span className="w-2 h-2 rounded-full bg-amber-500 ml-1 animate-pulse" />
  )
);

export default function StudySelectPage() {
  const { user, getAuthHeaders, logout, setSelectedStudy, selectedStudy } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateStudy, setShowCreateStudy] = useState(false);
  const [showStudyDetails, setShowStudyDetails] = useState(null);
  const [showEditStudy, setShowEditStudy] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [tabsCompleted, setTabsCompleted] = useState({ basic: false, details: false, timeline: false });
  
  // Sites and Countries management
  const [availableSites, setAvailableSites] = useState([]);
  const [availableCountries, setAvailableCountries] = useState(DEFAULT_COUNTRIES);
  const [newSiteName, setNewSiteName] = useState('');
  const [newSiteCode, setNewSiteCode] = useState('');
  const [newCountryName, setNewCountryName] = useState('');
  const [showAddSite, setShowAddSite] = useState(false);
  const [showAddCountry, setShowAddCountry] = useState(false);
  
  const [newStudy, setNewStudy] = useState({
    name: '',
    protocol_number: '',
    sponsor: '',
    phase: '',
    status: '',
    therapeutic_area: '',
    description: '',
    sites: [],
    countries: [],
    // Timeline milestones
    study_start_date: '',
    first_patient_in: '',
    last_patient_in: '',
    last_patient_out: '',
    database_lock: '',
    study_end_date: ''
  });

  const canCreateStudy = user?.role === 'admin' || user?.role === 'system_admin' || user?.role === 'tmf_lead' || user?.role === 'ctm';
  const isSystemAdmin = user?.role === 'system_admin' || user?.role === 'admin';

  // Validate tabs completion
  useEffect(() => {
    const basicComplete = newStudy.name.trim() !== '' && newStudy.protocol_number.trim() !== '' && newStudy.sponsor.trim() !== '';
    const detailsComplete = newStudy.therapeutic_area !== '' && newStudy.phase !== '' && newStudy.status !== '';
    const timelineComplete = newStudy.study_start_date !== '';
    
    setTabsCompleted({
      basic: basicComplete,
      details: detailsComplete,
      timeline: timelineComplete
    });
  }, [newStudy]);

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const { headers } = getAuthHeaders();
      const response = await axios.get(`${API_URL}/api/user-study-assignments/my-studies`, { headers });
      setAssignments(response.data?.assignments || []);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      toast.error('Failed to load study assignments');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const handleSelectStudy = (assignment) => {
    const studyContext = {
      study_id: assignment.study_id,
      study_name: assignment.study?.name || 'Unknown Study',
      protocol_number: assignment.study?.protocol_number || '',
      role: assignment.role,
      role_display: ROLE_DISPLAY_NAMES[assignment.role] || assignment.role
    };
    
    setSelectedStudy(studyContext);
    localStorage.setItem('selectedStudy', JSON.stringify(studyContext));
    
    toast.success(`Entered ${studyContext.study_name} as ${studyContext.role_display}`);
    navigate('/dashboard?view=all');
  };

  const resetNewStudy = () => {
    setNewStudy({
      name: '',
      protocol_number: '',
      sponsor: '',
      phase: '',
      status: '',
      therapeutic_area: '',
      description: '',
      sites: [],
      countries: [],
      study_start_date: '',
      first_patient_in: '',
      last_patient_in: '',
      last_patient_out: '',
      database_lock: '',
      study_end_date: ''
    });
    setActiveTab('basic');
    setTabsCompleted({ basic: false, details: false, timeline: false });
    setAvailableSites([]);
  };

  const handleCreateStudy = async () => {
    // Validate all tabs are completed
    if (!tabsCompleted.basic) {
      toast.error('Please complete Basic Info (Study Name, Protocol Number, Sponsor Name are required)');
      setActiveTab('basic');
      return;
    }
    if (!tabsCompleted.details) {
      toast.error('Please complete Details (Therapeutic Area, Phase, Status are required)');
      setActiveTab('details');
      return;
    }
    if (!tabsCompleted.timeline) {
      toast.error('Please complete Timeline (Study Start Date is required)');
      setActiveTab('timeline');
      return;
    }

    setCreateLoading(true);
    try {
      const { headers } = getAuthHeaders();
      const studyData = {
        ...newStudy,
        timeline: {
          study_start_date: newStudy.study_start_date || null,
          first_patient_in: newStudy.first_patient_in || null,
          last_patient_in: newStudy.last_patient_in || null,
          last_patient_out: newStudy.last_patient_out || null,
          database_lock: newStudy.database_lock || null,
          study_end_date: newStudy.study_end_date || null
        }
      };
      await axios.post(`${API_URL}/api/studies`, studyData, { headers });
      toast.success('Study created successfully');
      setShowCreateStudy(false);
      resetNewStudy();
      fetchAssignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create study');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleUpdateStudy = async () => {
    if (!showEditStudy) return;
    
    setCreateLoading(true);
    try {
      const { headers } = getAuthHeaders();
      const studyData = {
        name: newStudy.name,
        protocol_number: newStudy.protocol_number,
        sponsor: newStudy.sponsor,
        phase: newStudy.phase,
        status: newStudy.status,
        therapeutic_area: newStudy.therapeutic_area,
        description: newStudy.description,
        sites: newStudy.sites,
        countries: newStudy.countries,
        timeline: {
          study_start_date: newStudy.study_start_date || null,
          first_patient_in: newStudy.first_patient_in || null,
          last_patient_in: newStudy.last_patient_in || null,
          last_patient_out: newStudy.last_patient_out || null,
          database_lock: newStudy.database_lock || null,
          study_end_date: newStudy.study_end_date || null
        }
      };
      await axios.put(`${API_URL}/api/studies/${showEditStudy.study_id}`, studyData, { headers });
      toast.success('Study updated successfully');
      setShowEditStudy(null);
      resetNewStudy();
      fetchAssignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update study');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditStudyClick = (assignment) => {
    const study = assignment.study;
    setNewStudy({
      name: study?.name || '',
      protocol_number: study?.protocol_number || '',
      sponsor: study?.sponsor || '',
      phase: study?.phase || '',
      status: study?.status || '',
      therapeutic_area: study?.therapeutic_area || '',
      description: study?.description || '',
      sites: study?.sites || [],
      countries: study?.countries || [],
      study_start_date: study?.timeline?.study_start_date || '',
      first_patient_in: study?.timeline?.first_patient_in || '',
      last_patient_in: study?.timeline?.last_patient_in || '',
      last_patient_out: study?.timeline?.last_patient_out || '',
      database_lock: study?.timeline?.database_lock || '',
      study_end_date: study?.timeline?.study_end_date || ''
    });
    setAvailableSites(study?.sites || []);
    setShowEditStudy(assignment);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleAddSite = () => {
    if (!newSiteName.trim() || !newSiteCode.trim()) {
      toast.error('Site name and code are required');
      return;
    }
    const newSite = {
      site_id: `site_${Date.now()}`,
      site_code: newSiteCode.trim(),
      site_name: newSiteName.trim()
    };
    setAvailableSites(prev => [...prev, newSite]);
    setNewStudy(prev => ({ ...prev, sites: [...prev.sites, newSite] }));
    setNewSiteName('');
    setNewSiteCode('');
    setShowAddSite(false);
    toast.success('Site added');
  };

  const handleAddCountry = () => {
    if (!newCountryName.trim()) {
      toast.error('Country name is required');
      return;
    }
    if (!availableCountries.includes(newCountryName.trim())) {
      setAvailableCountries(prev => [...prev, newCountryName.trim()]);
    }
    if (!newStudy.countries.includes(newCountryName.trim())) {
      setNewStudy(prev => ({ ...prev, countries: [...prev.countries, newCountryName.trim()] }));
    }
    setNewCountryName('');
    setShowAddCountry(false);
    toast.success('Country added');
  };

  const toggleSiteSelection = (site) => {
    setNewStudy(prev => {
      const isSelected = prev.sites.some(s => s.site_id === site.site_id);
      if (isSelected) {
        return { ...prev, sites: prev.sites.filter(s => s.site_id !== site.site_id) };
      } else {
        return { ...prev, sites: [...prev.sites, site] };
      }
    });
  };

  const toggleCountrySelection = (country) => {
    setNewStudy(prev => {
      const isSelected = prev.countries.includes(country);
      if (isSelected) {
        return { ...prev, countries: prev.countries.filter(c => c !== country) };
      } else {
        return { ...prev, countries: [...prev.countries, country] };
      }
    });
  };

  const filteredAssignments = assignments.filter(a => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      a.study?.name?.toLowerCase().includes(query) ||
      a.study?.protocol_number?.toLowerCase().includes(query) ||
      a.study?.sponsor?.toLowerCase().includes(query) ||
      a.role?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="min-h-screen bg-[#F8FAFC] relative overflow-hidden max-w-full">
      {/* Header - Professional Slate Blue */}
      <header className="relative z-10 border-b border-neutral-200 bg-[#1E3A5F] shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src={ETMF_VAULT_LOGO} 
                alt="eTMF Vault" 
                className="w-28 sm:w-36 md:w-40 lg:w-44 h-auto object-contain"
                style={{ filter: 'brightness(1.1)' }}
              />
            </div>
            
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-sm font-medium text-white">{user?.name || user?.email}</p>
                <p className="text-xs text-white/60">{ROLE_DISPLAY_NAMES[user?.role] || user?.role}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-white/70 hover:text-white hover:bg-white/10"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-[#1E293B] mb-2">Select a Study</h2>
          <p className="text-neutral-500 max-w-lg mx-auto text-sm">
            Choose a study to access. Your role determines what actions you can perform within each study.
          </p>
        </div>

        {/* Search and Actions */}
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              type="text"
              placeholder="Search by study name, protocol, or sponsor..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white border-neutral-200 text-neutral-900 placeholder:text-neutral-400"
              data-testid="search-input"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchAssignments}
              className="border-neutral-200 text-neutral-700 hover:bg-neutral-50"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            
            {canCreateStudy && (
              <Button
                size="sm"
                onClick={() => setShowCreateStudy(true)}
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                data-testid="create-study-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add New Study
              </Button>
            )}
          </div>
        </div>

        {/* Studies Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" />
          </div>
        ) : filteredAssignments.length === 0 ? (
          <Card className="bg-white border-neutral-200 max-w-lg mx-auto shadow-sm">
            <CardContent className="p-12 text-center">
              <Building2 className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-neutral-900 mb-2">
                {searchQuery ? 'No Studies Found' : 'No Study Access'}
              </h3>
              <p className="text-neutral-500 mb-6">
                {searchQuery 
                  ? 'Try adjusting your search terms'
                  : 'You have not been assigned to any studies yet. Please contact your administrator.'}
              </p>
              {canCreateStudy && !searchQuery && (
                <Button onClick={() => setShowCreateStudy(true)} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Study
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredAssignments.map((assignment) => (
              <Card 
                key={`${assignment.study_id}-${assignment.role}`}
                className="bg-white border-neutral-200 hover:border-[#2563EB] transition-all shadow-sm hover:shadow-md"
                data-testid={`study-card-${assignment.study_id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-neutral-900 text-lg hover:text-[#2563EB] transition-colors">
                        {assignment.study?.name || 'Unknown Study'}
                      </CardTitle>
                      <CardDescription className="text-neutral-500 mt-1">
                        {assignment.study?.protocol_number || 'No Protocol'}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-1">
                      {isSystemAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleEditStudyClick(assignment); }}
                          className="text-neutral-400 hover:text-[#2563EB] p-1 h-auto"
                          title="Edit Study"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); setShowStudyDetails(assignment); }}
                        className="text-neutral-400 hover:text-[#2563EB] p-1 h-auto"
                        title="View Details"
                      >
                        <Info className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Your Role */}
                  <div className="flex items-center justify-between p-2 bg-neutral-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-[#2563EB]" />
                      <span className="text-xs text-neutral-600">Your Role</span>
                    </div>
                    <Badge className="bg-[#2563EB]/10 text-[#1D4ED8] border-[#2563EB]/20 text-xs">
                      {ROLE_DISPLAY_NAMES[assignment.role] || assignment.role}
                    </Badge>
                  </div>
                  
                  {/* Therapeutic Area */}
                  {assignment.study?.therapeutic_area && (
                    <div className="flex items-center gap-2 text-xs">
                      <Target className="w-3.5 h-3.5 text-[#1E3A5F]" />
                      <span className="text-neutral-600">{assignment.study.therapeutic_area}</span>
                    </div>
                  )}
                  
                  {/* Study Info */}
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-3 h-3 text-neutral-400" />
                      <span className="text-neutral-500 truncate">{assignment.study?.sponsor || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Activity className="w-3 h-3 text-neutral-400" />
                      <span className="text-neutral-500">{assignment.study?.phase || 'N/A'}</span>
                    </div>
                  </div>
                  
                  {/* Status & Enter Button */}
                  <div className="flex items-center justify-between pt-2">
                    <Badge 
                      className={`${STATUS_COLORS[assignment.study?.status] || 'bg-neutral-500'} text-white capitalize text-xs`}
                    >
                      {assignment.study?.status || 'Unknown'}
                    </Badge>
                    <Button
                      size="sm"
                      onClick={() => handleSelectStudy(assignment)}
                      className="bg-[#1E3A5F] hover:bg-[#152B47] h-7 text-xs text-white"
                    >
                      Enter Study
                      <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Footer Info */}
        <div className="mt-8 text-center">
          <p className="text-xs text-neutral-400">
             eTMF Vault •  A Product of <a href="https://www.vetforcesolutions.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">Vetforce Solutions</a>
          </p>
        </div>
      </main>

      {/* Create/Edit Study Dialog */}
      <Dialog open={showCreateStudy || !!showEditStudy} onOpenChange={(open) => { 
        if (!open) { 
          setShowCreateStudy(false); 
          setShowEditStudy(null); 
          resetNewStudy(); 
        } 
      }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>{showEditStudy ? 'Edit Study' : 'Add New Study'}</DialogTitle>
            <DialogDescription className="text-white/60">
              {showEditStudy 
                ? 'Update study information. All fields can be modified.'
                : 'Create a new clinical study. Complete all three tabs before creating.'}
            </DialogDescription>
          </DialogHeader>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-slate-900/50 border-slate-600 w-full grid grid-cols-3">
              <TabsTrigger value="basic" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-white/70 flex items-center">
                Basic Info
                <TabStatus completed={tabsCompleted.basic} />
              </TabsTrigger>
              <TabsTrigger value="details" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-white/70 flex items-center">
                Details
                <TabStatus completed={tabsCompleted.details} />
              </TabsTrigger>
              <TabsTrigger value="timeline" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-white/70 flex items-center">
                Timeline
                <TabStatus completed={tabsCompleted.timeline} />
              </TabsTrigger>
            </TabsList>
            
            <ScrollArea className="max-h-[50vh] mt-4">
              {/* Basic Info Tab - Only Study Name, Protocol Number, Sponsor */}
              <TabsContent value="basic" className="space-y-4 m-0">
                <div>
                  <Label className="text-white">Study Name <span className="text-red-400">*</span></Label>
                  <Input
                    value={newStudy.name}
                    onChange={(e) => setNewStudy(p => ({ ...p, name: e.target.value }))}
                    placeholder="e.g., ABC-123 Phase III Trial"
                    className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-400"
                    data-testid="input-study-name"
                  />
                </div>
                
                <div>
                  <Label className="text-white">Protocol Number <span className="text-red-400">*</span></Label>
                  <Input
                    value={newStudy.protocol_number}
                    onChange={(e) => setNewStudy(p => ({ ...p, protocol_number: e.target.value }))}
                    placeholder="e.g., ABC-123-001"
                    className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-400"
                    data-testid="input-protocol"
                  />
                </div>
                
                <div>
                  <Label className="text-white">Sponsor Name <span className="text-red-400">*</span></Label>
                  <Input
                    value={newStudy.sponsor}
                    onChange={(e) => setNewStudy(p => ({ ...p, sponsor: e.target.value }))}
                    placeholder="e.g., PharmaCorp Inc."
                    className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-400"
                    data-testid="input-sponsor"
                  />
                </div>
              </TabsContent>
              
              {/* Details Tab - Therapeutic Area, Phase, Status, Sites, Countries */}
              <TabsContent value="details" className="space-y-4 m-0">
                <div>
                  <Label className="text-white">Therapeutic Area <span className="text-red-400">*</span></Label>
                  <Select value={newStudy.therapeutic_area || ''} onValueChange={(v) => setNewStudy(p => ({ ...p, therapeutic_area: v }))}>
                    <SelectTrigger className="bg-slate-900/50 border-slate-600 text-white [&>span]:text-white">
                      <SelectValue placeholder="Select therapeutic area" className="text-white" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600">
                      {THERAPEUTIC_AREAS.map(area => (
                        <SelectItem key={area} value={area} className="text-white hover:bg-slate-700 focus:bg-slate-700">{area}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white">Phase <span className="text-red-400">*</span></Label>
                    <Select value={newStudy.phase} onValueChange={(v) => setNewStudy(p => ({ ...p, phase: v }))}>
                      <SelectTrigger className="bg-slate-900/50 border-slate-600 text-white [&>span]:text-white" data-testid="select-phase">
                        <SelectValue placeholder="Select phase" className="text-white" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-600">
                        <SelectItem value="Phase I" className="text-white hover:bg-slate-700 focus:bg-slate-700">Phase I</SelectItem>
                        <SelectItem value="Phase II" className="text-white hover:bg-slate-700 focus:bg-slate-700">Phase II</SelectItem>
                        <SelectItem value="Phase III" className="text-white hover:bg-slate-700 focus:bg-slate-700">Phase III</SelectItem>
                        <SelectItem value="Phase IV" className="text-white hover:bg-slate-700 focus:bg-slate-700">Phase IV</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-white">Status <span className="text-red-400">*</span></Label>
                    <Select value={newStudy.status} onValueChange={(v) => setNewStudy(p => ({ ...p, status: v }))}>
                      <SelectTrigger className="bg-slate-900/50 border-slate-600 text-white [&>span]:text-white" data-testid="select-status">
                        <SelectValue placeholder="Select status" className="text-white" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-600">
                        <SelectItem value="planning" className="text-white hover:bg-slate-700 focus:bg-slate-700">Planning</SelectItem>
                        <SelectItem value="active" className="text-white hover:bg-slate-700 focus:bg-slate-700">Active</SelectItem>
                        <SelectItem value="paused" className="text-white hover:bg-slate-700 focus:bg-slate-700">Paused</SelectItem>
                        <SelectItem value="closed" className="text-white hover:bg-slate-700 focus:bg-slate-700">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Sites Multi-Select */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-white flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Sites
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAddSite(true)}
                      className="text-blue-400 hover:text-blue-300 h-6 text-xs"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add New Site
                    </Button>
                  </div>
                  
                  {showAddSite && (
                    <div className="p-3 bg-slate-900/70 rounded-lg mb-2 space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Site Code (e.g., US-001)"
                          value={newSiteCode}
                          onChange={(e) => setNewSiteCode(e.target.value)}
                          className="bg-slate-800 border-slate-600 text-white text-sm h-8"
                        />
                        <Input
                          placeholder="Site Name"
                          value={newSiteName}
                          onChange={(e) => setNewSiteName(e.target.value)}
                          className="bg-slate-800 border-slate-600 text-white text-sm h-8"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleAddSite} className="bg-blue-600 hover:bg-blue-700 h-7 text-xs">
                          <Check className="w-3 h-3 mr-1" />
                          Add
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setShowAddSite(false)} className="text-white/70 h-7 text-xs">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <div className="bg-slate-900/50 border border-slate-600 rounded-lg p-2 max-h-32 overflow-y-auto">
                    {availableSites.length === 0 ? (
                      <p className="text-white/40 text-xs text-center py-2">No sites added yet. Click &quot;Add New Site&quot; above.</p>
                    ) : (
                      <div className="space-y-1">
                        {availableSites.map((site) => (
                          <div
                            key={site.site_id}
                            className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                              newStudy.sites.some(s => s.site_id === site.site_id)
                                ? 'bg-blue-600/30 border border-blue-500/50'
                                : 'bg-slate-800/50 hover:bg-slate-700/50'
                            }`}
                            onClick={() => toggleSiteSelection(site)}
                          >
                            <Checkbox
                              checked={newStudy.sites.some(s => s.site_id === site.site_id)}
                              className="border-slate-500"
                            />
                            <span className="text-white text-sm">{site.site_code} - {site.site_name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {newStudy.sites.length > 0 && (
                    <p className="text-xs text-blue-400 mt-1">{newStudy.sites.length} site(s) selected</p>
                  )}
                </div>

                {/* Countries Multi-Select */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-white flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Countries
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAddCountry(true)}
                      className="text-blue-400 hover:text-blue-300 h-6 text-xs"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add New Country
                    </Button>
                  </div>
                  
                  {showAddCountry && (
                    <div className="p-3 bg-slate-900/70 rounded-lg mb-2 space-y-2">
                      <Input
                        placeholder="Country Name"
                        value={newCountryName}
                        onChange={(e) => setNewCountryName(e.target.value)}
                        className="bg-slate-800 border-slate-600 text-white text-sm h-8"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleAddCountry} className="bg-blue-600 hover:bg-blue-700 h-7 text-xs">
                          <Check className="w-3 h-3 mr-1" />
                          Add
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setShowAddCountry(false)} className="text-white/70 h-7 text-xs">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <div className="bg-slate-900/50 border border-slate-600 rounded-lg p-2 max-h-32 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-1">
                      {availableCountries.map((country) => (
                        <div
                          key={country}
                          className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                            newStudy.countries.includes(country)
                              ? 'bg-blue-600/30 border border-blue-500/50'
                              : 'bg-slate-800/50 hover:bg-slate-700/50'
                          }`}
                          onClick={() => toggleCountrySelection(country)}
                        >
                          <Checkbox
                            checked={newStudy.countries.includes(country)}
                            className="border-slate-500"
                          />
                          <span className="text-white text-xs">{country}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {newStudy.countries.length > 0 && (
                    <p className="text-xs text-blue-400 mt-1">{newStudy.countries.length} country/countries selected</p>
                  )}
                </div>
                
                <div>
                  <Label className="text-white">Description</Label>
                  <Textarea
                    value={newStudy.description}
                    onChange={(e) => setNewStudy(p => ({ ...p, description: e.target.value }))}
                    placeholder="Brief description of the study objectives and design..."
                    className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-400 min-h-[80px]"
                    data-testid="input-description"
                  />
                </div>
              </TabsContent>
              
              {/* Timeline Tab */}
              <TabsContent value="timeline" className="space-y-4 m-0">
                <p className="text-xs text-white/50 mb-4">
                  <Milestone className="w-3 h-3 inline mr-1" />
                  Define key study milestones for document tracking
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white">Study Start Date <span className="text-red-400">*</span></Label>
                    <Input
                      type="date"
                      value={newStudy.study_start_date}
                      onChange={(e) => setNewStudy(p => ({ ...p, study_start_date: e.target.value }))}
                      className="bg-slate-900/50 border-slate-600 text-white [color-scheme:dark]"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white">First Patient In (FPI)</Label>
                    <Input
                      type="date"
                      value={newStudy.first_patient_in}
                      onChange={(e) => setNewStudy(p => ({ ...p, first_patient_in: e.target.value }))}
                      className="bg-slate-900/50 border-slate-600 text-white [color-scheme:dark]"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white">Last Patient In (LPI)</Label>
                    <Input
                      type="date"
                      value={newStudy.last_patient_in}
                      onChange={(e) => setNewStudy(p => ({ ...p, last_patient_in: e.target.value }))}
                      className="bg-slate-900/50 border-slate-600 text-white [color-scheme:dark]"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white">Last Patient Out (LPO)</Label>
                    <Input
                      type="date"
                      value={newStudy.last_patient_out}
                      onChange={(e) => setNewStudy(p => ({ ...p, last_patient_out: e.target.value }))}
                      className="bg-slate-900/50 border-slate-600 text-white [color-scheme:dark]"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white">Database Lock</Label>
                    <Input
                      type="date"
                      value={newStudy.database_lock}
                      onChange={(e) => setNewStudy(p => ({ ...p, database_lock: e.target.value }))}
                      className="bg-slate-900/50 border-slate-600 text-white [color-scheme:dark]"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white">Study End Date</Label>
                    <Input
                      type="date"
                      value={newStudy.study_end_date}
                      onChange={(e) => setNewStudy(p => ({ ...p, study_end_date: e.target.value }))}
                      className="bg-slate-900/50 border-slate-600 text-white [color-scheme:dark]"
                    />
                  </div>
                </div>
              </TabsContent>
            </ScrollArea>
          </Tabs>
          
          <DialogFooter className="mt-4">
            <div className="flex items-center justify-between w-full">
              <div className="text-xs text-white/50">
                {!showEditStudy && (
                  <>
                    {Object.values(tabsCompleted).filter(Boolean).length}/3 sections completed
                  </>
                )}
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => { 
                    setShowCreateStudy(false); 
                    setShowEditStudy(null); 
                    resetNewStudy(); 
                  }} 
                  className="border-slate-600 text-white hover:bg-slate-700"
                >
                  Cancel
                </Button>
                {showEditStudy ? (
                  <Button 
                    onClick={handleUpdateStudy} 
                    disabled={createLoading} 
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {createLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Update Study
                  </Button>
                ) : (
                  <Button 
                    onClick={handleCreateStudy} 
                    disabled={createLoading || !Object.values(tabsCompleted).every(Boolean)} 
                    className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50" 
                    data-testid="submit-create-study"
                  >
                    {createLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Create Study
                  </Button>
                )}
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Study Details Dialog */}
      <Dialog open={!!showStudyDetails} onOpenChange={(open) => !open && setShowStudyDetails(null)}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{showStudyDetails?.study?.name || 'Study Details'}</DialogTitle>
            <DialogDescription className="text-white/60">
              {showStudyDetails?.study?.protocol_number}
            </DialogDescription>
          </DialogHeader>
          
          {showStudyDetails && (
            <div className="space-y-4 py-4">
              {/* Role */}
              <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                <span className="text-white/70">Your Role</span>
                <Badge className="bg-blue-600/30 text-blue-300">{ROLE_DISPLAY_NAMES[showStudyDetails.role]}</Badge>
              </div>
              
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-2 bg-slate-900/30 rounded">
                  <p className="text-white/50 text-xs">Sponsor</p>
                  <p className="text-white">{showStudyDetails.study?.sponsor || 'N/A'}</p>
                </div>
                <div className="p-2 bg-slate-900/30 rounded">
                  <p className="text-white/50 text-xs">Phase</p>
                  <p className="text-white">{showStudyDetails.study?.phase || 'N/A'}</p>
                </div>
                <div className="p-2 bg-slate-900/30 rounded">
                  <p className="text-white/50 text-xs">Status</p>
                  <Badge className={`${STATUS_COLORS[showStudyDetails.study?.status]} text-white capitalize`}>
                    {showStudyDetails.study?.status}
                  </Badge>
                </div>
                <div className="p-2 bg-slate-900/30 rounded">
                  <p className="text-white/50 text-xs">Therapeutic Area</p>
                  <p className="text-white">{showStudyDetails.study?.therapeutic_area || 'N/A'}</p>
                </div>
              </div>
              
              {/* Sites */}
              {showStudyDetails.study?.sites?.length > 0 && (
                <div className="p-3 bg-slate-900/30 rounded">
                  <p className="text-white/50 text-xs mb-2 flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> Sites ({showStudyDetails.study.sites.length})
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {showStudyDetails.study.sites.map((site, idx) => (
                      <Badge key={idx} variant="outline" className="text-white/70 border-slate-600 text-xs">
                        {site.site_code}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Countries */}
              {showStudyDetails.study?.countries?.length > 0 && (
                <div className="p-3 bg-slate-900/30 rounded">
                  <p className="text-white/50 text-xs mb-2 flex items-center gap-1">
                    <Globe className="w-3 h-3" /> Countries ({showStudyDetails.study.countries.length})
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {showStudyDetails.study.countries.map((country, idx) => (
                      <Badge key={idx} variant="outline" className="text-white/70 border-slate-600 text-xs">
                        {country}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Description */}
              {showStudyDetails.study?.description && (
                <div className="p-3 bg-slate-900/30 rounded">
                  <p className="text-white/50 text-xs mb-1">Description</p>
                  <p className="text-white text-sm">{showStudyDetails.study.description}</p>
                </div>
              )}
              
              {/* Timeline */}
              {showStudyDetails.study?.timeline && (
                <div className="p-3 bg-slate-900/30 rounded">
                  <p className="text-white/50 text-xs mb-2 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Timeline Milestones
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {showStudyDetails.study.timeline.study_start_date && (
                      <div><span className="text-white/50">Start:</span> <span className="text-white">{showStudyDetails.study.timeline.study_start_date}</span></div>
                    )}
                    {showStudyDetails.study.timeline.first_patient_in && (
                      <div><span className="text-white/50">FPI:</span> <span className="text-white">{showStudyDetails.study.timeline.first_patient_in}</span></div>
                    )}
                    {showStudyDetails.study.timeline.last_patient_in && (
                      <div><span className="text-white/50">LPI:</span> <span className="text-white">{showStudyDetails.study.timeline.last_patient_in}</span></div>
                    )}
                    {showStudyDetails.study.timeline.last_patient_out && (
                      <div><span className="text-white/50">LPO:</span> <span className="text-white">{showStudyDetails.study.timeline.last_patient_out}</span></div>
                    )}
                    {showStudyDetails.study.timeline.database_lock && (
                      <div><span className="text-white/50">DB Lock:</span> <span className="text-white">{showStudyDetails.study.timeline.database_lock}</span></div>
                    )}
                    {showStudyDetails.study.timeline.study_end_date && (
                      <div><span className="text-white/50">End:</span> <span className="text-white">{showStudyDetails.study.timeline.study_end_date}</span></div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStudyDetails(null)} className="border-slate-600 text-white hover:bg-slate-700">
              Close
            </Button>
            <Button onClick={() => { handleSelectStudy(showStudyDetails); setShowStudyDetails(null); }} className="bg-blue-600 hover:bg-blue-700">
              Enter Study
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
